import { NgStyle } from '@angular/common'
import * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../../state'

@ngCore.Component({
  imports: [NgStyle],
  selector: 'atn-summit-pulse-breakdown',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** {@link state.summit.pulse.Feature} */
  public readonly pulseFeature = ngCore.inject(state.summit.pulse.Feature)

  /**
   * Derived signal calculating the container dimensions
   * based on the stacked circle sizes.
   */
  public readonly containerHeightSg = ngCore.computed(
    (
      breakdown = this.pulseFeature.selectors.engagementBreakdown.asSignal(),
    ) => {
      if (!breakdown) {
        return 0
      }

      const items = this._itemsBase(breakdown)
      const sizes = items.map(i => i.size)

      // Largest and smallest circle determine vertical stacking limit
      const largest = Math.max(...sizes)
      const smallest = Math.min(...sizes)

      // Container height = max possible stacked height
      return largest + smallest
    },
  )

  /**
   * Signal computing the final display data for each circle.
   * Each item includes a `styles` object
   * containing position, size, color, and layering (z-index).
   */
  public readonly itemsSg = ngCore.computed(
    (
      breakdown = this.pulseFeature.selectors.engagementBreakdown.asSignal(),
      containerHeight = this.containerHeightSg(),
    ) => {
      if (!breakdown || containerHeight === 0) {
        return []
      }

      // Generate base circle data (size, color, etc.)
      const items = this._itemsBase(breakdown)
      const sizes = items.map(i => i.size)

      // Assign sizes to variables with default fallbacks
      const s0 = sizes[0] ?? 120 // largest circle
      const s1 = sizes[1] ?? 90 // second largest
      const s2 = sizes[2] ?? 70 // third largest
      const s3 = sizes[3] ?? 60 // smallest

      // Circle 0: left margin + vertically centered
      const left0 = 0
      const top0 = 0

      // Circle 1: slightly overlaps circle 0 horizontally, small vertical shift
      const overlapX = Math.round(Math.min(s0, s1) * 0.1) // 10% overlap
      const left1 = left0 + s0 - overlapX
      const top1 = Math.round(top0 + 10)

      // Circle 2: diagonal shift relative to circle 1
      const left2 = Math.round(left1 + (s1 - s2) * 0.1)
      const top2 = Math.round(top1 + s1 * 0.8)

      // Circle 3: overlaps circle 2 mostly, slight downward shift
      const overlapLeft = Math.round(Math.min(s2, s3) * 0.9)
      const left3 = Math.round(left2 - overlapLeft)
      const top3 = Math.round(top2 + s2 * 0.1)

      // z-index determines which circle appears on top
      const positions = [
        { left: left0, top: top0, zIndex: 10 },
        { left: left1, top: top1, zIndex: 11 },
        { left: left2, top: top2, zIndex: 12 },
        { left: left3, top: top3, zIndex: 13 },
      ]

      // Merge position data into each circle's styles object
      return items.map((item, i) => ({
        ...item,
        styles: {
          ...item.styles,
          left: `${positions[i].left}px`,
          top: `${positions[i].top}px`,
          zIndex: positions[i].zIndex.toString(),
        } satisfies Partial<CSSStyleDeclaration>,
      }))
    },
  )

  /**
   * Generates base item data
   */
  private _itemsBase(breakdown: ents.core.summit.Pulse.EngagementBreakdown) {
    // Only use numeric keys (exclude summary)
    const keys = Object.keys(breakdown).filter(
      k => k !== 'summary',
    ) as (keyof ents.core.summit.Pulse.EngagementBreakdown)[]

    // Predefined colors for the circles
    const colorPalette = [
      ds.color.Scale.SHADOW.darker,
      ds.color.Scale.SHADOW.dark,
      ds.color.Scale.SHADOW.neutral,
      ds.color.Scale.SUCCESS.dark,
    ]

    const rawItems = keys.map((key, i) => {
      const percent = breakdown[key] as number
      const size = this._sizeFor(percent)
      const color = colorPalette[i % colorPalette.length]

      return {
        key,
        label: this._getLabel(key),
        percent,
        size,
        styles: {
          background: color,
          height: `${size}px`,
          width: `${size}px`,
        } satisfies Partial<CSSStyleDeclaration>,
      }
    })

    return rawItems.sort((a, b) => b.percent - a.percent)
  }

  /**
   * Calculates the visual circle diameter for a given percentage.
   *  Ensures that each circle remains between given min and max.
   */
  private _sizeFor(percent: number): number {
    const min = 100
    const max = 260
    return Math.min(max, Math.max(min, percent * 6))
  }

  /**
   * Helper method to return human-readable labels for each engagement type
   */
  private _getLabel(
    key: keyof ents.core.summit.Pulse.EngagementBreakdown,
  ): string {
    const labels: Record<string, string> = {
      atRisk: 'At Risk',
      disengaged: 'Disengaged',
      engaged: 'Engaged',
      summary: '',
      thriving: 'Thriving',
    }
    return labels[key] ?? key
  }
}
